"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  PairedDataInput,
  ResultTable,
  usePairedData,
} from "@/components/tools/stats-descriptive/ui";
import {
  BootCards,
  BootPlot,
  BootWorking,
  intervalRows,
  percentLabel,
  statName,
} from "@/components/tools/stats-descriptive-more/bootstrap-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberList } from "@/lib/parse-number";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";
import {
  type BootStatistic,
  type BootstrapReport,
  bootstrapReport,
  makeStat,
} from "@/lib/tool-kit/stats-descriptive-more/bootstrap";

/** English strings. A translated content file overrides these through `ui`. */
export const BOOTSTRAP_CONFIDENCE_INTERVAL_CALCULATOR_STRINGS = {
  statLabel: "Statistic",
  statMean: "Mean",
  statMedian: "Median",
  statSd: "Standard deviation (n − 1)",
  statCorrelation: "Pearson correlation (paired data)",
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "2.1, 2.4, 2.6, 2.7, 2.9, 3, 3.2, 3.5, 3.9, 4.6, 5.8, 8.2",
  valuesDetected: "one: value detected|other: values detected",
  entryLabel: "Data entry",
  entryLists: "Separate X and Y lists",
  entryLines: 'Pairs — one "x, y" per line',
  xLabel: "X values",
  yLabel: "Y values",
  xPlaceholder: "1, 2, 3, 4, 5, 6, 7, 8",
  yPlaceholder: "2, 1, 4, 3, 7, 8, 5, 6",
  pairsLabel: "Paired data — one x, y pair per line",
  pairsPlaceholder: "1, 2\n2, 1\n3, 4",
  valuesCount: "one: value|other: values",
  pairsCount: "one: pair read|other: pairs read",
  confidenceLabel: "Confidence level",
  conf90: "90%",
  conf95: "95%",
  conf99: "99%",
  resamplesLabel: "Number of resamples",
  seedLabel: "Random seed",
  seedHint: "Same data, settings and seed always give the same intervals.",
  newSeed: "New random seed",
  run: "Run bootstrap",
  running: "Resampling…",
  tryExample: "Try an example",
  tryPairs: "Try a correlation example",
  exampleData: "2.1, 2.4, 2.6, 2.7, 2.9, 3, 3.2, 3.5, 3.9, 4.6, 5.8, 8.2",
  exampleX: "1, 2, 3, 4, 5, 6, 7, 8, 9, 10",
  exampleY: "2.3, 1.9, 4.2, 3.1, 6.8, 5.2, 7.9, 6.1, 9.3, 8.4",
  clear: "Clear",
  enterPrompt: "Enter your data, then press “Run bootstrap”.",
  errorTooFew:
    "Need more data: at least 2 values (3 for a standard deviation or correlation) with some spread, and an equal number of x and y values for a correlation.",
  errorTooMany:
    "That is more than {max} values. Use a sample of at most {max}, or a summary statistic you can compute yourself.",
  errorSeed: "The seed must be a whole number from 0 to 4,294,967,295.",
  stale:
    "The inputs have changed since this run. Press “Run bootstrap” to update the results.",
  results: "Results",
  cardSe: "Bootstrap standard error",
  cardBias: "Bias (mean of resamples − estimate)",
  cardPercentile: "{level} percentile interval",
  cardBca: "{level} BCa interval",
  rangeTemplate: "{lower} to {upper}",
  notAvailable: "—",
  intervalsTitle: "Intervals compared",
  colMethod: "Method",
  colLower: "Lower",
  colUpper: "Upper",
  colNote: "Note",
  methodPercentile: "Percentile",
  methodBca: "BCa (bias-corrected and accelerated)",
  methodBasic: "Basic (reverse percentile)",
  methodNormal: "Normal approximation",
  notePercentile: "The middle of the resampled statistics.",
  noteBca: "z₀ = {z0}, acceleration a = {a}. Recommended for most uses.",
  noteBcaFailed:
    "Not computable: every resample fell on one side of the estimate.",
  noteBasic: "Can fall outside the possible range of the statistic.",
  noteNormal:
    "Estimate − bias ± z × standard error. Assumes a bell-shaped distribution.",
  plotTitle: "Distribution of the resampled statistic",
  plotLabel:
    "Histogram of {count} bootstrap replicates with the estimate at {estimate} and the interval from {lower} to {upper}.",
  plotY: "Resamples",
  markEstimate: "Estimate",
  markBca: "BCa",
  plotNote:
    "Solid line: the estimate. Labelled lines: the BCa interval. Dashed lines: the percentile interval.",
  workingTitle: "How this was calculated",
  workSetup:
    "n = {n} values, {resamples} resamples with replacement, seed {seed}",
  workSe:
    "Bootstrap SE = standard deviation of the resampled statistics = {se}; bias = {bias}",
  workPercentile:
    "Percentile: the values at ranks (B + 1) × {alpha} and (B + 1) × (1 − {alpha}) of the sorted resamples (rank {rank} for the lower end) → {lower} to {upper}",
  workZ0:
    "z₀ = Φ⁻¹(share of resamples below the estimate) = Φ⁻¹({below} ÷ {count}) = {z0}",
  workA:
    "Acceleration a = Σd³ ÷ (6 (Σd²)^1.5) from the leave-one-out (jackknife) estimates = {a}",
  workAdjusted:
    "Adjusted percentiles Φ(z₀ + (z₀ + z) ÷ (1 − a(z₀ + z))) = {low} and {high} → {lower} to {upper}",
  workDropped:
    "{count} resamples were left out because the statistic was undefined for them (a correlation needs both variables to vary).",
  workRng:
    "Resampling uses a seeded SFC32 generator, so results repeat exactly here but are not digit-for-digit what R’s set.seed gives; they agree within Monte Carlo error.",
  copySummary: "Copy summary",
  copied: "Copied!",
  summaryText:
    "{statistic} = {estimate}, {level} BCa CI [{lower}, {upper}] ({method}), {resamples} resamples, seed {seed}",
  summaryPercentile: "percentile",
  summaryBca: "BCa",
} as const;

const MAX_N = 2000;
const MAX_SEED = 4_294_967_295;

interface Snapshot {
  key: string;
  report: BootstrapReport;
}

export default function BootstrapConfidenceIntervalCalculatorWidget() {
  const strings = useToolUi(BOOTSTRAP_CONFIDENCE_INTERVAL_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [statistic, setStatistic] = useState<BootStatistic>("mean");
  const [dataText, setDataText] = useState("");
  const pairs = usePairedData();
  const [confidence, setConfidence] = useState("0.95");
  const [resamples, setResamples] = useState("5000");
  const [seedText, setSeedText] = useState("12345");
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCorrelation = statistic === "correlation";
  const values = isCorrelation ? pairs.xs : parseNumberList(dataText);
  const key = JSON.stringify([
    statistic,
    values,
    isCorrelation ? pairs.ys : [],
    confidence,
    resamples,
    seedText,
  ]);
  const num = (value: number) => formatStat(fmt, value);
  const view = { strings, fmt, num };

  function run() {
    const seed = parseIntegerInput(seedText);
    const spec = makeStat(statistic, values, isCorrelation ? pairs.ys : []);
    if (seed === null || seed < 0 || seed > MAX_SEED) {
      setError(strings.errorSeed);
    } else if (values.length > MAX_N) {
      setError(t(strings.errorTooMany, { max: fmt(MAX_N) }));
    } else if (spec === null) {
      setError(strings.errorTooFew);
    } else {
      setError(null);
      setRunning(true);
      setTimeout(() => {
        const report = bootstrapReport(spec, statistic, {
          resamples: Number(resamples),
          seed,
          confidence: Number(confidence),
        });
        setSnapshot({ key, report });
        setRunning(false);
      }, 20);
    }
  }

  function newSeed() {
    const buffer = new Uint32Array(1);
    crypto.getRandomValues(buffer);
    setSeedText(String(buffer[0]));
  }

  const report = snapshot?.report ?? null;
  const level = report ? percentLabel(report.confidence, view) : "";
  const preferred = report ? (report.bca ?? report.percentile) : null;

  return (
    <>
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="bs-stat"
            label={strings.statLabel}
            onChange={setStatistic}
            options={[
              { value: "mean", label: strings.statMean },
              { value: "median", label: strings.statMedian },
              { value: "sd", label: strings.statSd },
              { value: "correlation", label: strings.statCorrelation },
            ]}
            value={statistic}
          />
          <SelectField
            id="bs-conf"
            label={strings.confidenceLabel}
            onChange={setConfidence}
            options={[
              { value: "0.9", label: strings.conf90 },
              { value: "0.95", label: strings.conf95 },
              { value: "0.99", label: strings.conf99 },
            ]}
            value={confidence}
          />
          <SelectField
            id="bs-resamples"
            label={strings.resamplesLabel}
            onChange={setResamples}
            options={["1000", "2000", "5000", "10000", "20000"].map(
              (value) => ({
                value,
                label: fmt(Number(value)),
              })
            )}
            value={resamples}
          />
        </div>
        {isCorrelation ? (
          <PairedDataInput
            data={pairs}
            idPrefix="bs"
            labels={{
              mode: strings.entryLabel,
              lists: strings.entryLists,
              lines: strings.entryLines,
              x: strings.xLabel,
              y: strings.yLabel,
              xPlaceholder: strings.xPlaceholder,
              yPlaceholder: strings.yPlaceholder,
              pairs: strings.pairsLabel,
              pairsPlaceholder: strings.pairsPlaceholder,
            }}
            pairsHint={
              <>
                {fmt(pairs.pairCount)}
                {tPlural(strings.pairsCount, locale, pairs.pairCount)}
              </>
            }
            xHint={
              <>
                {fmt(pairs.listXCount)}
                {tPlural(strings.valuesCount, locale, pairs.listXCount)}
              </>
            }
            yHint={
              <>
                {fmt(pairs.listYCount)}
                {tPlural(strings.valuesCount, locale, pairs.listYCount)}
              </>
            }
          />
        ) : (
          <TextAreaField
            hint={
              <>
                {fmt(values.length)}
                {tPlural(strings.valuesDetected, locale, values.length)}
              </>
            }
            id="bs-data"
            label={strings.dataLabel}
            onChange={setDataText}
            placeholder={strings.dataPlaceholder}
            value={dataText}
          />
        )}
        <div className="grid items-end gap-4 sm:grid-cols-[1fr_auto]">
          <TextField
            hint={strings.seedHint}
            id="bs-seed"
            label={strings.seedLabel}
            numeric
            onChange={setSeedText}
            value={seedText}
          />
          <button className={BUTTON_SECONDARY} onClick={newSeed} type="button">
            {strings.newSeed}
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={running}
            onClick={run}
            type="button"
          >
            {running ? strings.running : strings.run}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setStatistic("mean");
              setDataText(strings.exampleData);
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setStatistic("correlation");
              pairs.fill({
                x: strings.exampleX,
                y: strings.exampleY,
                pairs: "",
              });
            }}
            type="button"
          >
            {strings.tryPairs}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setDataText("");
              pairs.clear();
              setSnapshot(null);
              setError(null);
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {error ? <ErrorNotice>{error}</ErrorNotice> : null}
      {report && snapshot && preferred ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {snapshot.key === key ? null : <Notice>{strings.stale}</Notice>}
          <div aria-live="polite" className="space-y-4">
            <BootCards report={report} view={view} />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copySummary}
              text={t(strings.summaryText, {
                statistic: statName(report, strings),
                estimate: num(report.estimate),
                level,
                lower: num(preferred.lower),
                upper: num(preferred.upper),
                method: report.bca
                  ? strings.summaryBca
                  : strings.summaryPercentile,
                resamples: fmt(report.resamples),
                seed: fmt(report.seed, { useGrouping: false }),
              })}
            />
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.intervalsTitle}</h3>
            <ResultTable
              head={[
                strings.colMethod,
                strings.colLower,
                strings.colUpper,
                strings.colNote,
              ]}
              rows={intervalRows(report, view)}
              wrapFirstColumn
            />
          </div>
          <BootPlot report={report} view={view} />
          <BootWorking report={report} view={view} />
        </section>
      ) : null}
      {report || error ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
